import { useEffect, useState } from "react";
import { ModeBadge } from "@/components/figures/ModeBadge";
import {
  announced,
  award,
  initiatives,
  lastChecked,
  milestones,
} from "@/data/rhtp";

function formatMillions(value: number) {
  return `$${(value / 1_000_000).toFixed(1)}M`;
}

function announcedFor(key: string) {
  const total = announced
    .filter((a) => a.initiative === key)
    .reduce((sum, a) => sum + a.amount, 0);
  return total > 0 ? formatMillions(total) : "—";
}

function hawaiiToday() {
  // en-CA yields YYYY-MM-DD
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Pacific/Honolulu",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function daysRemaining(iso: string, today: string) {
  const target = Date.parse(`${iso}T00:00:00Z`);
  const now = Date.parse(`${today}T00:00:00Z`);
  return Math.round((target - now) / 86_400_000);
}

function formatTarget(iso: string) {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

const countdownDates = ["2026-10-30", "2027-01-01", "2027-01-20"];

export function RHTPClock({ compact = false }: { compact?: boolean }) {
  const announcedTotal = announced.reduce((sum, a) => sum + a.amount, 0);
  const percent = (announcedTotal / award.amount) * 100;
  const [today, setToday] = useState<string | null>(null);
  useEffect(() => {
    if ((window as unknown as { __PRERENDER__?: boolean }).__PRERENDER__) return;
    setToday(hawaiiToday());
  }, []);
  const sorted = [...initiatives].sort((a, b) => b.bp1 - a.bp1);

  const countdowns = countdownDates
    .map((date) => milestones.find((m) => m.date === date))
    .filter((m): m is NonNullable<typeof m> => Boolean(m));

  return (
    <div className="rounded-xl border border-border/60 bg-card/60 p-5 sm:p-7">
      {!compact && (
        <h2 className="font-display text-2xl font-bold text-foreground">
          The RHTP Clock
        </h2>
      )}

      <div className={`${compact ? "" : "mt-5"} flex flex-wrap items-baseline gap-x-6 gap-y-2`}>
        <p className="font-display text-xl font-bold text-foreground sm:text-2xl">
          {award.label} awarded for Budget Period 1
        </p>
        <p className="font-mono text-[13px] text-liberty-gold">
          {formatMillions(announcedTotal)} announced to date ({percent.toFixed(1)}%)
        </p>
      </div>

      <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-border/50">
        <div
          className="h-full rounded-full bg-liberty-gold"
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </div>

      {!compact && (
        <div className="mt-8 overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-border/60">
                <th className="py-2 pr-4 font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                  Initiative
                </th>
                <th className="py-2 pr-4 font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                  Lead
                </th>
                <th className="py-2 pr-4 font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                  BP1 allocation
                </th>
                <th className="py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                  Announced to date
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((item) => (
                <tr key={item.key} className="border-b border-border/40">
                  <td className="py-3 pr-4 font-medium text-foreground">
                    {item.key}
                  </td>
                  <td className="py-3 pr-4 text-foreground/80">{item.lead}</td>
                  <td className="py-3 pr-4 font-mono text-foreground">
                    {formatMillions(item.bp1)}
                  </td>
                  <td className="py-3 font-mono text-liberty-gold">
                    {announcedFor(item.key)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className={`${compact ? "mt-5" : "mt-8"} grid gap-4 sm:grid-cols-3`}>
        {countdowns.map((milestone) => {
          const days = today ? daysRemaining(milestone.date, today) : null;
          return (
            <div
              key={milestone.date}
              className="rounded-lg border border-border/60 bg-background/40 p-4"
            >
              <p className="font-display text-2xl font-bold text-foreground">
                {days === null
                  ? formatTarget(milestone.date)
                  : days < 0
                    ? "passed"
                    : `${days} days`}
              </p>
              <p className="mt-2 text-[12px] leading-relaxed text-muted-foreground">
                {milestone.label}
                {days === null ? `: ${formatTarget(milestone.date)}` : ""}
              </p>
            </div>
          );
        })}
      </div>

      {!compact && (
        <div className="mt-6 space-y-2 border-t border-border/60 pt-5">
          <p className="text-[13px] text-foreground/80">
            Updates on public announcements only. Last checked 15 September 2026.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-[12px] leading-relaxed text-muted-foreground">
              Sources: CMS; Office of the Governor releases of 6 January and 1
              September 2026; Hawaiʻi RHTP briefing, September 2026
            </p>
            <ModeBadge mode="DIRECT" />
          </div>
          <p className="sr-only">Last checked {lastChecked}.</p>
        </div>
      )}
    </div>
  );
}
